import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

const defaultDormerSettings = {
  type: "DORMER_SETTINGS",
  eaveFriezeBoard: "NONE",
};

/**
 * Dormer-specific settings that previously existed on RoofSettings.
 * These will be moved to a new DormerSettings type.
 */
const roofDormerSettingsToMigrate: { [key: string]: string } = {
  dormerEaveReturnDistance: "eaveReturnDistance",
  dormerRakeFriezeBoard: "rakeFriezeBoard",
  dormerRakeFriezeBoardMaterial: "rakeFriezeBoardMaterial",
  dormerEaveFriezeBoard: "eaveFriezeBoard",
  dormerEaveFriezeBoardMaterial: "eaveFriezeBoardMaterial",
  dormerWallBaseBoardMode: "wallBaseBoardMode",
  dormerWallBaseBoard: "wallBaseBoard",
  dormerBellyBandBoard: "gableBellyBandBoard",
  dormerBellyBandBoardAnchor: "gableBellyBandBoardAnchor",
  dormerBellyBandBoardJustification: "gableBellyBandBoardJustification",
  dormerBellyBandBoardOffset: "gableBellyBandBoardOffset",
  dormerWrapFriezeBoardMode: "wrapFriezeBoardMode",
};

/**
 * Dormer-specific settings that previously existed on the top-level RoofDormerPlacement.
 * These will be moved to a new dormerSettings property of type DormerSettings.
 */
const dormerInstanceSettings: { [key: string]: boolean } = {
  roofType: true,
  slope: true,
  slopes: true,
  wallMaterial: true,
  roofingMaterial: true,
  roofingMaterialThickness: true,
  roofingMaterialExtension: true,
  roofRidgeRoofingCapMaterial: true,
  roofRidgeRoofingCapWidth: true,
  roofRidgeRoofingCapThickness: true,
  eaveType: true,
  eaveCutType: true,
  eaveReturnDistance: true,
  rakeBracketPropId: true,
  friezeBoardOrientation: true,
  rakeFriezeBoard: true,
  rakeFriezeBoardMaterial: true,
  eaveFriezeBoard: true,
  eaveFriezeBoardMaterial: true,
  wrapFriezeBoardMode: true,
  addRafterTailExtension: true,
  rafterTailSlope: true,
  rafterTailExtensionEdgeOffset: true,
  rafterTailExtension: true,
  rafterTailDepth: true,
  rafterTailClosed: true,
  eaveReturnRoofMaterial: true,
  lookoutRafters: true,
  flyRafters: true,
  overhang: true,
  // includeRidgeVent: true // determine if this should be moved to dormerSettings?
  includeTriangleGableVent: true,
  triangleGableVentBaseWidth: true,
  cornerBoardProfile: true,
  cornerBoardMaterial: true,
  cornerBoardBottomOffset: true,
  cornerBoardTopOffset: true,
  wallBaseBoardMode: true,
  wallBaseBoard: true,
  gableBellyBandBoard: true,
  gableBellyBandBoardAnchor: true,
  gableBellyBandBoardJustification: true,
  gableBellyBandBoardOffset: true,
};

// need to initially read from the roof's settings?
// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_244: DocMigration = {
  maxSchemaVersion: "0.0.244",
  description: "Migrate dormer-specific roof settings from home to new dormer settings type",
  apply(json: any) {
    for (const id in json.settings) {
      const settings = json.settings[id];

      if (settings.type !== "ROOF_SETTINGS") {
        continue;
      }

      const migratedSettings = {
        id: uid(),
        entityType: "SETTINGS",
        type: "DORMER_SETTINGS",
        name: "Dormer Settings",
        parentId: settings.parentId,
      };

      // Migrate dormer-specific settings from RoofSettings to DormerSettings
      for (const key in settings) {
        if (!(key in roofDormerSettingsToMigrate)) {
          continue;
        }

        const migratedKey = roofDormerSettingsToMigrate[key];

        if (settings[key] === defaultDormerSettings[migratedKey]) {
          delete settings[key];
          continue;
        }

        migratedSettings[migratedKey] = settings[key];
        delete settings[key];
      }

      // Only include the migrated settings if at least one dormer-related setting was migrated (not counting base settings properties)
      if (Object.keys(migratedSettings).length > 5) {
        json.settings[migratedSettings.id] = migratedSettings;
      }
    }

    // Migrate dormer-specific settings from RoofDormerPlacement to nested dormerSettings partial
    for (const id in json.dormers) {
      const dormer = json.dormers[id];

      for (const key in dormer) {
        if (key in dormerInstanceSettings) {
          dormer.dormerSettings = dormer.dormerSettings || {};
          dormer.dormerSettings[key] = dormer[key];
          delete dormer[key];
        }
      }
    }

    return json;
  },
};

export default migration_0_0_244;
